'use client';
import HubHandoffLink from '@dr-pam/common-components/Components/Auth/HubHandoffLink';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { IconMenu2, IconX, IconSearch } from '@tabler/icons-react';
import AuthLinks from './AuthLinks';
import { usePathname } from 'next/navigation';
import SearchForm from '../search/SearchForm';
import Image from 'next/image';
import DropDownTextMenu from './navbar/DropDownTextMenu';
import DropDownMenu from './navbar/DropDownMenu';
import ParentsOrMyPossumsMenu from './ParentsOrPossumsMenu';
import ProfessionalsMenu from './ProfessionalsMenu';
import useAuthenticatedUser from '@dr-pam/common-components/Hooks/useAuthenticatedUser';
import { isNdcInstitutePath } from '../../utils/navTheme';

export enum SubMenu {
	NONE = '',
	FOR_PARENTS = 'for parents',
	FOR_PROFESSIONALS = 'for providers',
	ABOUT = 'about',
	FREE_RESOURCES = 'free resources',
	CLINICAL_CONSULTATIONS = 'consultations',
	LOGIN = 'login',
	ACCOUNT = 'account',
	SEARCH = 'search',
	SIGN_UP = 'sign up',
	CURRENCY = 'currency',
}

export default function NavBar({
	ndcHubUrl,
	hasActivePaidNdcAccess,
}: {
	ndcHubUrl?: string;
	hasActivePaidNdcAccess?: boolean;
}) {
	const [showNavbar, setShowNavbar] = useState(false);
	const [activeSubMenu, setActiveSubMenu] = useState<SubMenu>(SubMenu.NONE);
	const [collapsed, setCollapsed] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const menuRef = useRef<null | HTMLDivElement>(null);
	const pathname = usePathname();
	const { user } = useAuthenticatedUser();

	const isFormOpen =
		activeSubMenu === SubMenu.SEARCH || activeSubMenu === SubMenu.LOGIN || activeSubMenu === SubMenu.SIGN_UP;
	const isNdcMember = !!user && !!hasActivePaidNdcAccess;
	const isNdcTheme = isNdcInstitutePath(pathname) && (!user || !!hasActivePaidNdcAccess);
	// The open search/login panel is light, so the NDC logo and back arrow go purple there.
	const ndcBackIcon = isNdcTheme ? '/images/icons/ndc-icons/Left-Direction.svg' : undefined;

	const toggleSubMenu = (item: SubMenu) => {
		setActiveSubMenu(activeSubMenu === item ? SubMenu.NONE : item);
	};

	useEffect(() => {
		const check = () => setIsMobile(window.innerWidth <= 1384);
		check();
		window.addEventListener('resize', check);
		return () => window.removeEventListener('resize', check);
	}, []);

	const toggleMenu = () => {
		setActiveSubMenu(SubMenu.NONE);
		setShowNavbar((open) => {
			const next = !open;
			document.body.classList.toggle('menu-open', next);
			return next;
		});
	};

	const closeMenu = () => {
		setShowNavbar(false);
		document.body.classList.remove('menu-open');
	};

	useEffect(() => {
		setActiveSubMenu(SubMenu.NONE);
		closeMenu();
	}, [pathname]);

	const closeNavbar = useCallback(() => {
		setActiveSubMenu(SubMenu.NONE);
		closeMenu();
	}, []);

	const closeSearch = useCallback(() => {
		setActiveSubMenu(SubMenu.NONE);
	}, []);

	const closeLogin = useCallback(() => {
		setActiveSubMenu(SubMenu.NONE);
	}, []);

	const closeSignUp = useCallback(() => {
		setActiveSubMenu(SubMenu.NONE);
	}, []);

	useEffect(() => {
		const handleScroll = () => setCollapsed(window.scrollY > 50);
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		document.body.classList.toggle('small', collapsed);
	}, [collapsed]);

	return (
		<header className={`navbar dark-bg ${collapsed ? 'small' : ''}`} ref={menuRef}>
			<div className="navbar-container">
				<Link className="logo-container" href="/" onClick={closeNavbar}>
					<Image
						src={
							isNdcTheme
								? isMobile && isFormOpen
									? '/images/logos/logo-navbar_possums-ndc-purple.svg'
									: '/images/logos/logo-navbar_possums-ndc.svg'
								: isMobile && isFormOpen
									? '/images/logos/possums_logo-full-color.svg'
									: '/images/logos/possums_logo-dark_bg.svg'
						}
						className="icon-logo"
						alt="Logo - The Possums baby and toddler sleep program."
						width={0}
						height={0}
						unoptimized
					/>
				</Link>

				<nav>
					<div className="hamburger-menu" onClick={toggleMenu}>
						{!showNavbar ? <IconMenu2 size={32} /> : <IconX size={32} />}
					</div>

					<div className={`menu-container ${showNavbar ? 'show' : ''}`}>
						<div className="menu-links">
							<ParentsOrMyPossumsMenu
								active={activeSubMenu === SubMenu.FOR_PARENTS}
								onToggle={() => toggleSubMenu(SubMenu.FOR_PARENTS)}
								onNavigate={closeNavbar}
								isProfessional={isNdcMember}
							/>
							<ProfessionalsMenu
								active={activeSubMenu === SubMenu.FOR_PROFESSIONALS}
								onToggle={() => toggleSubMenu(SubMenu.FOR_PROFESSIONALS)}
								onNavigate={closeNavbar}
								ndcHubUrl={ndcHubUrl}
								coursesOnly={isNdcMember}
							/>

							<DropDownTextMenu
								title="about"
								active={activeSubMenu === SubMenu.ABOUT}
								onToggle={() => toggleSubMenu(SubMenu.ABOUT)}
							>
								<Link className="nav-link" href="/science" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/TheScienceBehindPossums.svg"
										height={30}
										width={30}
										alt="menu icon the science"
									/>
									the science behind possums/ndc
								</Link>
								<Link className="nav-link" href="/about-us" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/WhoWeAre.svg"
										height={30}
										width={30}
										alt="menu icon who we are"
									/>
									who we are
								</Link>
								<Link className="nav-link" href="/books-by-drpam" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/DrPamsBooks.svg"
										height={30}
										width={30}
										alt="menu icon dr pam's books"
										className="books"
									/>
									dr pam&apos;s books
								</Link>
							</DropDownTextMenu>
							<DropDownTextMenu
								title="free resources"
								active={activeSubMenu === SubMenu.FREE_RESOURCES}
								onToggle={() => toggleSubMenu(SubMenu.FREE_RESOURCES)}
							>
								<Link className="nav-link" href="/free-resources" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/FreeResources.svg"
										height={30}
										width={30}
										alt="menu icon free resources"
									/>
									free resources
								</Link>
								<Link className="nav-link" href="/podcasts" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/Podcast-dark-blue.svg"
										height={30}
										width={30}
										alt="menu icon podcast"
									/>
									podcasts
								</Link>
								<Link className="nav-link" href="/blog" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/DrPamsBlog.svg"
										height={30}
										width={30}
										alt="menu icon dr pam's blog"
									/>
									dr pam&apos;s blog
								</Link>
							</DropDownTextMenu>
						</div>
						<div className="menu-links">
							<Link className="nav-item" href="/ndc-evidence-base" onClick={closeNavbar}>
								<span className="drop-btn">ndc evidence base</span>
							</Link>
							<DropDownTextMenu
								title="consultations"
								active={activeSubMenu === SubMenu.CLINICAL_CONSULTATIONS}
								onToggle={() => toggleSubMenu(SubMenu.CLINICAL_CONSULTATIONS)}
							>
								<Link className="nav-link" href="/ndc-status" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/TheNdcRegister.svg"
										height={30}
										width={30}
										alt="menu icon the ndc register"
									/>
									the ndc register
								</Link>
								<Link className="nav-link" href="/find-an-ndc-practitioner" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/FindANdcAccreditedPractitioner.svg"
										height={30}
										width={30}
										alt="menu icon find a NDC accredited practitioner"
									/>
									<div>
										<span className="nav-link-main">find an ndc accredited</span>
										<br />
										<span className="nav-link-sub">practitioner</span>
									</div>
								</Link>
								<Link className="nav-link" href="/clinics" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/FindAPossumsClinic.svg"
										height={30}
										width={30}
										alt="menu icon find a possums clinic"
									/>
									find a possums clinic
								</Link>
								<Link className="nav-link" href="/consult-with-drpam" onClick={closeNavbar}>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src="/images/icons/dark-icons/ConsultWithDrPam.svg"
										height={30}
										width={30}
										alt="menu icon consult with dr pam"
									/>
									consult with dr pam
								</Link>
							</DropDownTextMenu>
							<div className="mobile-spacer hidden-on-desktop"></div>
							{user && hasActivePaidNdcAccess && ndcHubUrl && (
								<HubHandoffLink
									hubUrl={ndcHubUrl}
									target="_blank"
									rel="noreferrer"
									className="auth-links signup-link nav-item custom-title education-hub-link"
								>
									<div className="icon-wrapper">
										{/* eslint-disable-next-line @next/next/no-img-element */}
										<img
											src="/images/icons/ndc-icons/education-hub_icon_white.svg"
											className="icon-normal"
											width={25}
											height={25}
											alt=""
											aria-hidden="true"
										/>
										{/* eslint-disable-next-line @next/next/no-img-element */}
										<img
											src="/images/icons/dark-icons/login-icon_dark.svg"
											className="icon-hover"
											width={25}
											height={25}
											alt=""
											aria-hidden="true"
										/>
									</div>
									<span>education hub</span>
								</HubHandoffLink>
							)}
							<AuthLinks
								activeMenu={activeSubMenu}
								onClick={(item) => {
									toggleSubMenu(item);
								}}
								onCloseLogin={closeLogin}
								onCloseSignUp={closeSignUp}
								onNavigate={closeNavbar}
								backIcon={ndcBackIcon}
							/>

							<div className="mobile-spacer hidden-on-desktop"></div>
							<DropDownMenu
								active={activeSubMenu === SubMenu.SEARCH}
								className="toggle-search"
								onToggle={() => toggleSubMenu(SubMenu.SEARCH)}
								header={
									<>
										<IconSearch />
										<div className="mobile-only search-link">search</div>
									</>
								}
							>
								<SearchForm onClose={closeSearch} backIcon={ndcBackIcon} />
							</DropDownMenu>
							<div className="mobile-spacer hidden-on-desktop"></div>
							{user && (
								<Link
									href="/logout"
									className="auth-links logout-link nav-item mobile-only custom-title"
								>
									Logout
								</Link>
							)}
						</div>
					</div>
				</nav>
			</div>
		</header>
	);
}
